import { useEffect, useState } from "react";
import { Box, IconButton, Paper, Tooltip } from "@mui/material";
import {
  PauseCircle,
  PlayCircle,
  SkipNext,
  SkipPrevious,
} from "@mui/icons-material";
import backSixSeconds from "../../svg/backSixSeconds.svg";
import forwardSixSeconds from "../../svg/forwardSixSeconds.svg";
import { DAWZoomSlider } from "./DAWZoomSlider";
import { DAWVolumeSlider } from "./DAWVolumeSlider";

function sliderToZoom(sliderValue) {
  // Map 0-100 to our log range
  const minZoom = 0.01;
  const maxZoom = 500;
  // Calculate using exponential scale
  const minLog = Math.log(minZoom);
  const maxLog = Math.log(maxZoom);
  const scale = (maxLog - minLog) / 100;

  return Math.exp(minLog + scale * sliderValue);
}

function zoomToSlider(zoomValue) {
  // Reverse: convert zoom back to slider position
  const minZoom = 0.01;
  const maxZoom = 500;
  const minLog = Math.log(minZoom);
  const maxLog = Math.log(maxZoom);

  return ((Math.log(zoomValue) - minLog) / (maxLog - minLog)) * 100;
}

export const DAWControls = ({ daw, currentTime, isPlaying, zoom }) => {
  const [zoomVal, setZoomVal] = useState(zoomToSlider(daw?.getZoom() || 20));

  const onZoomChange = (value) => {
    daw.setZoom(sliderToZoom(value));
    setZoomVal(value);
  };

  const onVolumeChange = (value) => {
    daw.getTracks().forEach((track) => {
      daw.setTrackVolume(track.id, value / 100);
    });
  };

  const onJumpBack = () => {
    const newTime = currentTime - 6;
    newTime < 0 ? daw.seek(0) : daw.seek(newTime);
  };

  const onJumpForward = () => {
    const newTime = currentTime + 6;
    newTime > daw.getDuration()
      ? daw.seek(daw.getDuration())
      : daw.seek(newTime);
  };

  const onSkipStart = () => daw.seek(0);

  const onSkipEnd = () => daw.seek(daw.getDuration());

  useEffect(() => {
    setZoomVal(zoomToSlider(zoom));
  }, [zoom]);

  return (
    <Paper
      elevation={4}
      sx={{
        display: "flex",
        px: { xs: 1, lg: 10 },
      }}
    >
      <Box
        display="flex"
        justifyContent="flex-start"
        flex={1}
        sx={{ gap: { xs: 1, lg: 5 } }}
      >
        <DAWZoomSlider value={zoomVal} onChange={onZoomChange} />
      </Box>
      <Box display="flex" justifyContent="center" flex={1}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Tooltip title="Skip to Start">
            <IconButton
              aria-label="skip to start"
              color="primary"
              onClick={() => onSkipStart()}
              size="small"
            >
              <SkipPrevious />
            </IconButton>
          </Tooltip>
          <Tooltip title="Back six seconds">
            <IconButton
              aria-label="back six seconds"
              onClick={() => onJumpBack()}
              size="small"
            >
              <img
                src={backSixSeconds}
                height={24}
                width={24}
                alt="back six seconds"
              />
            </IconButton>
          </Tooltip>
          {daw && !isPlaying && (
            <Tooltip title="Play">
              <IconButton
                aria-label="play"
                color="primary"
                onClick={() => daw.play()}
                size="small"
                sx={{ p: 0 }}
              >
                <PlayCircle sx={{ fontSize: 50 }} />
              </IconButton>
            </Tooltip>
          )}
          {daw && isPlaying && (
            <Tooltip title="Pause">
              <IconButton
                aria-label="pause"
                color="primary"
                onClick={() => daw.stop()}
                size="small"
                sx={{ p: 0 }}
              >
                <PauseCircle sx={{ fontSize: 50 }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Forward six seconds">
            <IconButton
              aria-label="forward six seconds"
              onClick={() => onJumpForward()}
              size="small"
            >
              <img
                src={forwardSixSeconds}
                height={24}
                width={24}
                alt="forward six seconds"
              />
            </IconButton>
          </Tooltip>
          <Tooltip title="Skip to End">
            <IconButton
              aria-label="skip to end"
              color="primary"
              onClick={() => onSkipEnd()}
              size="small"
            >
              <SkipNext />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Box display="flex" justifyContent="center" flex={1}>
        <DAWVolumeSlider
          value={daw?.getTracks()[0]?.volume * 100 || 100}
          onChange={onVolumeChange}
        />
      </Box>
    </Paper>
  );
};
